<template>
    <div class="sembonus-info">
        <Element font="font-heading-5" class="sembonus-info-title">
            {{ title }}
        </Element>

        <div class="sembonus-info-content">
            <Element
                font="font-body-m-bold"
                element="span"
                class="sembonus-info-content-text">
                {{ t({ key: 'sembonus.each_booked_night_gets_you_closer' }) }}
            </Element>

            <Link
                v-if="link"
                :href="link"
                :label="t({ key: 'shared.learn_more' })"
                :onClick="handleLinkClick"
                target="_blank"
                class="sembonus-info-content-link" />
        </div>

        <Element class="sembonus-info-notification">
            <Notification v-if="notification" :tier="2" padding="l" v-bind="notification" />
        </Element>
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { computed, defineComponent } from 'vue';
    import { isNil } from 'lodash';
    import { useStore } from '@/store';
    import { Bonus, HotelProduct } from '@/api/Cart/cart.interfaces';
    import { logStatsigCheckoutSembonusInteraction } from '@/tracking/statsig/checkout.functions';
    import { SemboUser } from '@/api/Members/members.interfaces';
    import { getReadMoreLink, getStampsToEarn } from '../../Sembonus.helpers';
    import { NotificationData, NotificationType } from './Info.interfaces';
    import { Market } from '@shared/config/market/market.models';
    import { Money } from '@libs/money/money.interfaces';
    import Element from '@ui/components/Element/Element.vue';
    import Link from '@ui/components/Link/Link.vue';
    import Notification from '@ui/components/Notification/Notification.vue';

    export default defineComponent({
        name: 'Info',
        methods: { t },
        components: { Element, Link, Notification },
        setup() {
            const store = useStore();

            const user = computed<SemboUser>(() => store.getters['auth/getUser']);
            const market = computed<Market>(() => store.getters['general/getMarket']);
            const bonus = computed<Bonus[]>(() => store.getters['cart/getAvailableBonus']);
            const hotels = computed<HotelProduct[]>(
                () => store.getters['cart/getHotelProducts']
            );

            const isAuthorized = computed<boolean>(() => !isNil(user.value));
            const isEligble = computed<boolean>(
                () => isAuthorized.value && user.value?.member?.canBeSembonusUser
            );

            const isSembonusUser = computed<boolean>(
                () => isEligble.value && user.value?.member?.isSembonusUser
            );

            const selectedBonus = computed<Money[]>(() => {
                return bonus.value
                    .filter((night) => night.IsApplied)
                    .map((night) => night.Value);
            });

            const link = computed<string | undefined>(() => getReadMoreLink(market.value));
            const stamps = computed<number>(() => getStampsToEarn(hotels.value));

            const title = computed<string>(() => {
                if (isSembonusUser.value) {
                    return 'Sembonus';
                } else {
                    return t({
                        key: 'sembonus.sembonus_x_nights_for_y_free',
                        values: [10, 1],
                    });
                }
            });

            const notificationType = computed<NotificationType>(() => {
                if (isAuthorized.value) {
                    if (isEligble.value) {
                        return selectedBonus.value.length
                            ? NotificationType.RedeemNight
                            : NotificationType.EarnStamps;
                    } else {
                        return NotificationType.NotEligible;
                    }
                } else {
                    return NotificationType.EarnStamps;
                }
            });

            const getEarnStampsTitle = (): string => {
                if (isSembonusUser.value) {
                    if (bonus.value.length) {
                        return t({
                            key: 'sembonus.you_will_earn_one_stamp',
                            keyPlural: 'sembonus.you_will_earn_x_stamps',
                            count: stamps.value,
                            values: [stamps.value],
                        });
                    } else {
                        return t({
                            key: 'sembonus.no_night_you_will_earn_x_stamps',
                            values: [stamps.value],
                        });
                    }
                } else {
                    return t({
                        key: 'sembonus.you_will_earn_one_stamp_opt_in',
                        keyPlural: 'sembonus.you_will_earn_x_stamps_opt_in',
                        count: stamps.value,
                        values: [stamps.value],
                    });
                }
            };

            const notification = computed<NotificationData | null>(() => {
                switch (notificationType.value) {
                    case NotificationType.EarnStamps:
                        return {
                            variant: 'positive',
                            icon: 'Offer',
                            title: getEarnStampsTitle(),
                        };
                    case NotificationType.RedeemNight:
                        return {
                            variant: 'neutral',
                            icon: 'InfoCircle',
                            title: t({ key: 'sembonus.when_you_redeem_nights' }),
                        };
                    case NotificationType.NotEligible:
                        return {
                            variant: 'warning',
                            icon: 'InfoCircle',
                            title: t({ key: 'sembonus.you_are_not_eligible_for_sembonus' }),
                        };
                    default:
                        return null;
                }
            });

            const handleLinkClick = () => {
                logStatsigCheckoutSembonusInteraction('ReadMore');
            };

            return { title, link, notification, handleLinkClick };
        },
    });
</script>

<style lang="scss" scoped>
    @forward 'Info';
</style>
